/**
 * The four sections, in one element, in two places.
 *
 * At 640px and up it is a row in the shell header, which is where a website
 * puts its main navigation. Under 640px `.sm-section-nav` in
 * `apps/app/src/index.css` fixes the same element to the bottom of the
 * viewport, where a fan's thumb already is (ISSUE-953 R1 and R2). The owner
 * reversed ISSUE-324 R2's "top at every width" rule on 2026-09-20 for phone
 * width only, so ISSUE-338's tablet and desktop rail still stands.
 *
 * It is ONE element that moves, never two with one hidden. A hidden copy has
 * to be kept out of the accessibility tree by hand, and the day it is not, a
 * screen reader reads the four sections twice (R9). Nothing here can drift
 * that way, because there is nothing to duplicate.
 *
 * The row wraps at large text sizes instead of stretching the page
 * sideways. The largest-text-scale browser check fails if this becomes
 * a single nowrap row again.
 *
 * Every class below is the class this file carried before ISSUE-953, byte for
 * byte. The phone-only overrides this card needs (no inline padding plus the
 * 12px semibold tab bar label, which together keep five items on one line,
 * and the active marker moving to the edge nearest the content) sit beside
 * the placement switch in the one
 * `@media (width < 40rem)` block in `apps/app/src/index.css`, NOT as `sm:`
 * utilities here.
 *
 * That is not a style preference, it is the only spelling that works.
 * `packages/theme/tailwind-preset.css` redefines `--breakpoint-md` and
 * `--breakpoint-lg` but declares no `--breakpoint-sm`, so Tailwind emits the
 * two redefined blocks BEFORE the built-in `sm` block at 40rem. A `sm:`
 * utility and a `md:` utility have equal specificity, so at 768px and up the
 * later one wins: `sm:px-3` here would silently beat the `md:px-4` on the
 * same line and shave 4px a side off every desktop item, which is exactly
 * what R2 forbids. That holds for any `sm:` paired with `md:` or `lg:` on one
 * property, anywhere in this app.
 *
 * Help is a fifth item at phone width only. It is an action, not a section:
 * a `<button>` that opens the support chat, never a link, so it never takes
 * `aria-current` and never shows the active marker, and the four links stay
 * the four links every test counts. Its classes are the inactive item's
 * classes, so it has the same tap target, label, spacing and focus ring. The
 * item is `hidden` by default and the phone block in `index.css` shows it,
 * the same technique the icons use. From 40rem up the provider's own floating
 * button is the way in, so the header row keeps exactly the four sections.
 */
import { FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { openSupportChat, setSupportHelpNavPresent } from "../supportWidget";
import { NavBadge } from "./NavBadge";
import { HelpCircleIcon, SectionIcon } from "./SectionIcon";
import { WEB_SECTIONS } from "./sections";
import { useShellBadges } from "./useShellBadges";

export const SectionNav = (): ReactNode => {
  const badges = useShellBadges();

  // While this bar (and its Help item) is on screen, the support chat's own
  // floating button steps aside at phone width; signed-out screens keep it.
  useEffect(() => {
    setSupportHelpNavPresent(window, true);
    return () => {
      setSupportHelpNavPresent(window, false);
    };
  }, []);

  return (
    <nav
      aria-label="Sections"
      className="sm-section-nav"
      data-testid="section-nav"
    >
      <ul className="flex flex-wrap items-stretch">
        {WEB_SECTIONS.map((section) => (
          <li className="min-w-0 flex-1 md:flex-none" key={section.key}>
            <Link
              to={section.to}
              activeOptions={{ exact: false, includeSearch: false }}
              className={`flex min-h-touch-target items-center justify-center gap-1 border-b-2 px-3 text-body-strong no-underline md:px-4 ${FOCUS_RING}`}
              activeProps={{
                "aria-current": "page",
                className: "border-primary-solid text-ink",
              }}
              inactiveProps={{
                className:
                  "border-transparent text-ink-secondary hover:text-ink",
              }}
            >
              <SectionIcon sectionKey={section.key} />
              <span className="inline-flex items-center gap-1">
                {section.label}
                {section.key === "pools" ? (
                  <NavBadge
                    count={badges.inviteCount}
                    singularLabel="invitation waiting"
                    pluralLabel="invitations waiting"
                  />
                ) : null}
                {section.key === "messages" ? (
                  <NavBadge
                    count={badges.messageUnreadCount}
                    singularLabel="unread message"
                    pluralLabel="unread messages"
                  />
                ) : null}
              </span>
            </Link>
          </li>
        ))}
        <li className="sm-nav-help hidden min-w-0 flex-1">
          <button
            type="button"
            aria-haspopup="dialog"
            className={`flex min-h-touch-target w-full cursor-pointer items-center justify-center gap-1 border-b-2 border-transparent px-3 text-body-strong text-ink-secondary hover:text-ink ${FOCUS_RING}`}
            onClick={() => {
              openSupportChat(window);
            }}
          >
            <HelpCircleIcon className="hidden size-icon-lg shrink-0" />
            <span className="inline-flex items-center gap-1">Help</span>
          </button>
        </li>
      </ul>
    </nav>
  );
};
